/**
 * SignalingClient — WebSocket 信令客户端
 * 连接信令服务器，处理匹配、offer/answer 交换
 */

export type SignalingRole = 'host' | 'joiner';

interface SignalingMessage {
  type: string;
  role?: SignalingRole;
  peerId?: string;
  roomId?: string;
  sdp?: string;
  from?: string;
  message?: string;
}

interface SignalingCallbacks {
  onConnected?: () => void;
  onRoomCreated?: (roomId: string) => void;
  onMatched?: (role: SignalingRole, roomId?: string) => void;
  onOffer?: (sdp: string) => void;
  onAnswer?: (sdp: string) => void;
  onPeerDisconnected?: () => void;
  onError?: (message: string) => void;
  onClose?: (event: CloseEvent, manual: boolean) => void;
}

export class SignalingClient {
  private ws: WebSocket | null = null;
  private url: string;
  private callbacks: SignalingCallbacks = {};
  private _connected = false;
  private manualClose = false;

  constructor(url: string) {
    this.url = url;
  }

  get isConnected(): boolean {
    return this._connected;
  }

  connect(): void {
    this.manualClose = false;
    try {
      this.ws = new WebSocket(this.url);
    } catch (e) {
      this.callbacks.onError?.('Failed to create WebSocket: ' + String(e));
      return;
    }

    this.ws.onopen = () => {
      this._connected = true;
      this.callbacks.onConnected?.();
    };

    this.ws.onmessage = (event) => {
      try {
        const msg = JSON.parse(event.data) as SignalingMessage;
        this.handleMessage(msg);
      } catch {
        // ignore non-JSON messages
      }
    };

    this.ws.onclose = (event) => {
      this._connected = false;
      this.callbacks.onClose?.(event, this.manualClose);
    };

    this.ws.onerror = () => {
      this.callbacks.onError?.('WebSocket connection error');
    };
  }

  private handleMessage(msg: SignalingMessage): void {
    switch (msg.type) {
      case 'room-created':
        if (msg.roomId) {
          this.callbacks.onRoomCreated?.(msg.roomId);
        }
        break;
      case 'matched':
        if (msg.role) {
          this.callbacks.onMatched?.(msg.role, msg.roomId);
        }
        break;
      case 'offer':
        if (msg.sdp) {
          this.callbacks.onOffer?.(msg.sdp);
        }
        break;
      case 'answer':
        if (msg.sdp) {
          this.callbacks.onAnswer?.(msg.sdp);
        }
        break;
      case 'peer-disconnected':
        this.callbacks.onPeerDisconnected?.();
        break;
      case 'error':
        this.callbacks.onError?.(msg.message || 'Unknown server error');
        break;
    }
  }

  sendMatch(): void {
    this.send({ type: 'match' });
  }

  sendCreateRoom(): void {
    this.send({ type: 'create-room' });
  }

  sendJoinRoom(roomId: string): void {
    this.send({ type: 'join-room', roomId });
  }

  sendOffer(sdp: string): void {
    this.send({ type: 'offer', sdp });
  }

  sendAnswer(sdp: string): void {
    this.send({ type: 'answer', sdp });
  }

  sendCancel(): void {
    this.send({ type: 'cancel' });
  }

  private send(data: object): void {
    if (this.ws && this.ws.readyState === WebSocket.OPEN) {
      this.ws.send(JSON.stringify(data));
    }
  }

  onConnected(cb: () => void): void {
    this.callbacks.onConnected = cb;
  }

  onRoomCreated(cb: (roomId: string) => void): void {
    this.callbacks.onRoomCreated = cb;
  }

  onMatched(cb: (role: SignalingRole, roomId?: string) => void): void {
    this.callbacks.onMatched = cb;
  }

  onOffer(cb: (sdp: string) => void): void {
    this.callbacks.onOffer = cb;
  }

  onAnswer(cb: (sdp: string) => void): void {
    this.callbacks.onAnswer = cb;
  }

  onPeerDisconnected(cb: () => void): void {
    this.callbacks.onPeerDisconnected = cb;
  }

  onError(cb: (message: string) => void): void {
    this.callbacks.onError = cb;
  }

  onClose(cb: (event: CloseEvent, manual: boolean) => void): void {
    this.callbacks.onClose = cb;
  }

  close(): void {
    this.manualClose = true;
    try {
      this.ws?.close();
    } catch {
      // ignore
    }
    this.ws = null;
    this._connected = false;
  }
}
